import {
  CNAPCGAAudit,
  Device,
} from '../components/insights/types/InsightsTypes';

/**
 * Prepare a fusion resource for export
 * @param resource the resource to export
 * @returns an object to be exported
 */
export const asExportable = (resource: any) => {
  const expResource = { ...resource };
  if (expResource.hasOwnProperty('__typename')) delete expResource.__typename;
  return expResource;
};

/**
 * Flatten a device object for export
 * @param dev Device resource
 * @returns object to be exported
 */
export const asExportableDevice = (dev: Device): any => {
  let flatObj: any = {};
  Object.keys(dev).forEach(key => {
    switch (key) {
      case 'osInfo': {
        const expOsInfo = asExportable(dev.osInfo);
        const osObj: any = {};
        Object.keys(expOsInfo).forEach(osKey => {
          const newKey = `os${osKey.charAt(0).toUpperCase() + osKey.slice(1)}`;
          osObj[newKey] = (expOsInfo as any)[osKey];
        });
        flatObj = { ...flatObj, ...osObj };
        break;
      }
      case 'applicationsPaged': {
        flatObj.applications = dev?.applicationsPaged?.applications
          ?.map(app => app.name)
          .join(',');
        break;
      }
      default:
        flatObj[key] = (dev as any)[key];
    }
  });
  return asExportable(flatObj);
};

/**
 * Flatten an array of devices for export
 * @param devices Device resources
 * @returns array of objects to be exported
 */
export const asExportableDevices = (devices: Device[]): any[] => {
  return devices.map(dev => asExportableDevice(dev));
};

/**
 * Flatten an array of devices for export for audit
 * @param devices Device resources
 * @returns array of objects to be exported
 */
export const asExportableDevicesForAudit = (devices: Device[]): any[] => {
  return devices.map((device: Device) => ({
    'Application Name': device.applicationsPaged?.applications[0]?.name,
    'Application Id': device.applicationsPaged?.applications[0]?.applicationId,
    FQDN: device.fqdn,
    Hostname: device.hostname,
    'Device Type': device.type,
    'Ip Address': device.ipAddress,
    'Operating System': device.osInfo.name,
    'OS Version/build': device.osInfo.version,
    'OS Make': device.osInfo.make,
    'Serial Number': device.serialNumber,
    Make: device.make,
    Model: device.model,
    'Technical Owner/POC':
      device.applicationsPaged?.applications[0]?.owner?.displayName,
  }));
};

/**
 * Escapes a value for safe inclusion in a CSV file.
 * Wraps the value in double quotes if it contains commas, quotes, or newlines, and escapes inner quotes.
 * @param value The value to escape for CSV.
 * @returns The escaped CSV value as a string.
 */
export const escapeCSVValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`; // escape inner quotes
  }
  return str;
};

/**
 * Prepare CNAP CGA audit data for export.
 * @param cnapData Array of CnapCGAAudit objects to be exported.
 * @returns Array of exportable CnapCGAAudit objects.
 */
export const asExportableCnapData = (cnapData: CNAPCGAAudit[]) => {
  return cnapData.map(item => ({
    'DevHub App Name': item.devhubAppName,
    'Application ID': item.applicationId,
    'Application Route': item.appRoute,
    'Application Name': item.appName,
    Subnet: item.subnet,
    Platform: item.platform,
    Timestamp: item.timestamp,
  }));
};

/**
 * Sort an array of devices by their type in ascending order.
 * @param devices Array of Device objects to be sorted.
 * @returns Sorted array of Device objects.
 */
export const sortDevices = (devices: Device[]): Device[] => {
  devices.sort((a, b) => {
    if (a.type < b.type) return -1;
    if (a.type > b.type) return 1;
    return 0;
  });
  return devices;
};
